import { Board } from "../board";
import { applyMove, legalMoves } from "../moves";
import { performRandomEnemyMove, type EnemyAction } from "../gameLogic";
import { BOARD_ROWS } from "../constants";
import { randInt } from "../rng";
import { boardToFen } from "./fen";
import { validateUciMove, randomChessJsMove } from "./chessjs";
import { StockfishEngine, difficultyForWraps } from "./engine";
import type { Piece, PieceType, Square } from "../types";

const PIECE_VALUE: Record<PieceType, number> = {
  pawn: 1, knight: 3, bishop: 3, rook: 5, queen: 9, king: 100,
};

/**
 * Drives the enemy turn by trying, in order:
 *   1. Pseudo-legal king capture (rogue mode only).
 *   2. Forced back-rank exit (so wraps are never blocked).
 *   3. Greedy capture — the best non-losing capture on the board.
 *   4. Stockfish best move (validated by chess.js and our own pseudo-legal rules).
 *   5. A random chess.js-legal move from the same position.
 *   6. The original random pseudo-legal move generator (last-resort fallback).
 *
 * Steps 5 and 6 guarantee the game keeps moving even if the engine is unavailable
 * or the position cannot be expressed in standard chess (e.g., missing king).
 *
 * Greedy capture (step 3) runs *before* Stockfish so the enemy never wanders past
 * a hanging piece — it always takes obvious material before consulting deep eval.
 */
export async function chooseEnemyMove(
  board: Board,
  engine: StockfishEngine | null,
  wraps: number,
  rng: () => number,
): Promise<EnemyAction | null> {
  // Rogue mode ends only when all pieces are gone; standard chess engines never
  // generate king-capture moves, so prefer a direct pseudo-legal king capture here.
  const kingCapture = findPseudoLegalKingCapture(board, rng);
  if (kingCapture) return kingCapture;

  // An enemy is not allowed to sit on the player's back rank and block wraps, so
  // any enemy that ended up there must spend this move leaving it.
  const backRankExit = findForcedBackRankExit(board, rng);
  if (backRankExit) return backRankExit;

  // Greedy capture before Stockfish so the enemy always takes obvious material.
  const greedy = findGreedyCapture(board, rng);
  if (greedy) return greedy;

  const fen = boardToFen(board, "enemy");

  if (engine) {
    const uci = await engine.bestMove(fen, difficultyForWraps(wraps));
    if (uci) {
      const applied = tryApplyUci(board, fen, uci);
      if (applied) return applied;
    }
  }

  const fallback = randomChessJsMove(fen, rng);
  if (fallback && isOwnPiece(board, fallback.from, "enemy") && isPseudoLegal(board, fallback.from, fallback.to)) {
    const captured = applyMove(board, fallback.from, fallback.to);
    return { from: fallback.from, to: fallback.to, captured };
  }

  return performRandomEnemyMove(board, rng);
}

/**
 * If any enemy piece occupies the player's back rank (row BOARD_ROWS-1), force one of
 * them to move off it this turn (preferring a capture). Returns null when no back-rank
 * enemy has a legal off-rank move — a rare boxed-in case that resolves once a neighbor
 * moves. This guarantees the enemy can never permanently block a wrap.
 */
export function findForcedBackRankExit(board: Board, rng: () => number): EnemyAction | null {
  const backRank = BOARD_ROWS - 1;
  const movers: Array<{ from: Square; offRank: Square[] }> = [];
  for (const { sq } of board.squaresOf("enemy")) {
    if (sq.row !== backRank) continue;
    const offRank = legalMoves(board, sq).filter((to) => to.row !== backRank);
    if (offRank.length > 0) movers.push({ from: sq, offRank });
  }
  if (movers.length === 0) return null;

  const mover = movers[randInt(rng, movers.length)];
  const captures = mover.offRank.filter((to) => board.get(to) !== null);
  const pool = captures.length > 0 ? captures : mover.offRank;
  const to = pool[randInt(rng, pool.length)];
  const captured = applyMove(board, mover.from, to);
  return { from: mover.from, to, captured };
}

/**
 * Best "obvious" capture for the enemy: grab the most valuable player piece, but
 * skip captures that lose material (a defended victim cheaper than the attacker).
 * Ties break toward using the cheaper attacker, then random. Returns null when no
 * worthwhile capture exists. Used as a fallback so the enemy never wanders past a
 * hanging piece when Stockfish is unavailable or the position isn't legal chess.
 */
export function findGreedyCapture(board: Board, rng: () => number): EnemyAction | null {
  let bestScore = -Infinity;
  let best: Array<{ from: Square; to: Square }> = [];
  for (const { sq, piece } of board.squaresOf("enemy")) {
    for (const to of legalMoves(board, sq)) {
      const victim = board.get(to);
      if (!victim) continue; // only captures
      const gain = PIECE_VALUE[victim.type];
      const cost = PIECE_VALUE[piece.type];
      // A defended victim worth less than our piece is a losing trade — skip it.
      if (gain < cost && isDefendedByPlayer(board, sq, to, piece)) continue;
      // Prefer big victims; tie-break toward the cheaper attacker.
      const score = gain * 100 - cost;
      if (score > bestScore) {
        bestScore = score;
        best = [{ from: sq, to }];
      } else if (score === bestScore) {
        best.push({ from: sq, to });
      }
    }
  }
  if (best.length === 0) return null;
  const pick = best[randInt(rng, best.length)];
  const captured = applyMove(board, pick.from, pick.to);
  return { from: pick.from, to: pick.to, captured };
}

/** True if any player piece could recapture on `to` after the enemy moves there. */
function isDefendedByPlayer(board: Board, from: Square, to: Square, attacker: Piece): boolean {
  const victim = board.get(to);
  board.place(from, null);
  board.place(to, attacker);
  let defended = false;
  for (const { sq } of board.squaresOf("player")) {
    if (legalMoves(board, sq).some((s) => s.row === to.row && s.col === to.col)) {
      defended = true;
      break;
    }
  }
  board.place(from, attacker);
  board.place(to, victim);
  return defended;
}

function findPseudoLegalKingCapture(board: Board, rng: () => number): EnemyAction | null {
  const candidates: Array<{ from: Square; to: Square }> = [];
  for (const { sq } of board.squaresOf("enemy")) {
    for (const to of legalMoves(board, sq)) {
      const target = board.get(to);
      if (!target) continue;
      if (target.side === "player" && target.type === "king") {
        candidates.push({ from: sq, to });
      }
    }
  }

  if (candidates.length === 0) return null;
  const pick = candidates[Math.floor(rng() * candidates.length)];
  const captured = applyMove(board, pick.from, pick.to);
  return { from: pick.from, to: pick.to, captured };
}

function tryApplyUci(board: Board, fen: string, uci: string): EnemyAction | null {
  const parsed = validateUciMove(fen, uci);
  if (!parsed) return null;
  const { from, to } = parsed;
  if (!isOwnPiece(board, from, "enemy")) return null;
  if (!isPseudoLegal(board, from, to)) return null;
  const captured = applyMove(board, from, to);
  return { from, to, captured };
}

function isOwnPiece(board: Board, sq: Square, side: "player" | "enemy"): boolean {
  const p = board.get(sq);
  return !!p && p.side === side;
}

function isPseudoLegal(board: Board, from: Square, to: Square): boolean {
  return legalMoves(board, from).some((s) => s.row === to.row && s.col === to.col);
}
